/**
 * KB view — list view rendering, state, and interaction handlers.
 */

import { navigate } from '../../router.js'
import { esc } from '../../shared/rendering.js'
import { fetchKBEntries, fetchKBReadCounts } from '../../api/kb.js'
import { _view, resetKbView } from './kb_usage.js'
import type { KBEntry } from '../../types/api.js'
import { kbTypeColor } from './kb_types.js'
import { action } from '../components/action-delegation.js'
import {
  btnStyle,
  renderKBRows,
  renderPaginationBar,
  renderTagCloud,
  renderTabBar,
} from './kb_list_render.js'

// ─── State ──────────────────────────────────────────────────────────────────

let _kbEntries: KBEntry[] = []
let _kbReadCounts: Record<string, number> = {}
let _kbTypeFilter: string | null = null
let _kbTagFilter = new Set<string>()
let _kbSearch = ''
let _kbSort: 'updated_desc' | 'updated_asc' | 'created_desc' | 'created_asc' = 'updated_desc'
let _kbPage = 0
let _kbLoadSeq = 0
let _kbSearchDebounce: ReturnType<typeof setTimeout> | null = null
// Pending tag to seed after renderKBListView() resets state (avoids state-before-reset race).
let _pendingTagFilter: string | null = null
const PAGE_SIZE = 25

// ─── DOM wiring ──────────────────────────────────────────────────────────────

function wireKbSearchInput(): void {
  const input = document.getElementById('kb-search') as HTMLInputElement | null
  if (!input) {
    return
  }
  input.addEventListener('input', () => {
    setKbSearch(input.value)
  })
}

// ─── Data helpers ────────────────────────────────────────────────────────────

function sortEntries(entries: readonly KBEntry[]): KBEntry[] {
  const sorted = [...entries]
  sorted.sort((a, b) => {
    const dir = _kbSort.endsWith('_asc') ? 1 : -1
    const field = _kbSort.startsWith('created') ? 'created_at' : 'updated_at'
    const av = a[field] ?? ''
    const bv = b[field] ?? ''
    if (av < bv) {
      return -1 * dir
    }
    if (av > bv) {
      return 1 * dir
    }
    return 0
  })
  return sorted
}

function getFilteredEntries(): KBEntry[] {
  let result = _kbEntries
  if (_kbTagFilter.size > 0) {
    result = result.filter((e) => (e.tags ?? []).some((t) => _kbTagFilter.has(t)))
  }
  return sortEntries(result)
}

// ─── List View Entry Point ───────────────────────────────────────────────────

export async function renderKBListView(): Promise<void> {
  const app = document.getElementById('app')
  if (!app) {
    return
  }
  // Reset ALL module-level state on route entry so prior visit state doesn't bleed through (SL-US02)
  _kbEntries = []
  _kbReadCounts = {}
  _kbTypeFilter = null
  _kbTagFilter = new Set()
  _kbSearch = ''
  _kbSort = 'updated_desc'
  _kbPage = 0
  if (_kbSearchDebounce) {
    clearTimeout(_kbSearchDebounce)
    _kbSearchDebounce = null
  }
  // Apply any pending tag seed set by goToKbWithTag() AFTER the reset above.
  if (_pendingTagFilter !== null) {
    _kbTagFilter = new Set([_pendingTagFilter])
    _pendingTagFilter = null
  }
  // Entering /kb always lands on Entries — see resetKbView().
  resetKbView()
  document.title = 'Knowledge Base \u2014 Grizzly'
  // Render the loading placeholder and focus immediately — focus fires on route entry, not
  // on filter re-renders (filter changes call loadKBList() directly, not renderKBListView).
  app.innerHTML =
    '<div id="kb-content"><div class="loading"><div class="spinner"></div></div></div>'
  // preventScroll: see fleet.ts focus() call — keeps sticky topbar pinned.
  app.focus({ preventScroll: true }) // SL-UA02: announce route change before async load
  await loadKBList()
}

async function loadKBList(): Promise<void> {
  const content = document.getElementById('kb-content')
  if (!content) {
    return
  }
  const seq = ++_kbLoadSeq
  try {
    const result = await fetchKBEntries({
      page_size: 500,
      search: _kbSearch || undefined,
      type: _kbTypeFilter || undefined,
      sort: _kbSort.startsWith('created') ? 'created_at' : 'updated_at',
      order: _kbSort.endsWith('_asc') ? 'asc' : 'desc',
    })
    if (seq !== _kbLoadSeq) {
      return
    }
    _kbEntries = result.data
    // Fetch read counts in batch (non-blocking: render immediately, update when ready).
    fetchKBReadCounts(result.data.map((e) => e.id))
      .then((counts) => {
        if (seq !== _kbLoadSeq) return
        _kbReadCounts = counts
        content.innerHTML = renderKBList()
        wireKbSearchInput()
      })
      .catch(() => {
        /* non-critical — list still works without read counts */
      })
    content.innerHTML = renderKBList()
    wireKbSearchInput()
  } catch (err) {
    if (seq !== _kbLoadSeq) {
      return
    }
    content.innerHTML = `<div class="empty">Failed to load KB entries. ${esc(String(err))}</div>`
  }
}

// ─── Rendering helpers ───────────────────────────────────────────────────────

function renderTypeButtons(): string {
  const types = Array.from(new Set(_kbEntries.map((e) => e.type))).sort()
  const byType: Record<string, number> = {}
  types.forEach((t) => {
    byType[t] = _kbEntries.filter((e) => e.type === t).length
  })
  const allBtn = `<button aria-pressed="${_kbTypeFilter === null}" style="${btnStyle(_kbTypeFilter === null)}" ${action('setKbTypeFilter', 'null')}>All types</button>`
  return (
    allBtn +
    types
      .map((t) => {
        const c = kbTypeColor(t)
        return `<button aria-pressed="${_kbTypeFilter === t}" style="${btnStyle(_kbTypeFilter === t)}" ${action('setKbTypeFilter', t)}>${esc(t)} <span style="font-size:9px;opacity:.6;padding:1px 5px;border-radius:99px;background:var(--${c}-d);color:var(--${c})">${byType[t]}</span></button>`
      })
      .join('')
  )
}

function renderSortButtons(): string {
  const btn = (key: typeof _kbSort, label: string) =>
    `<button aria-pressed="${_kbSort === key}" style="${btnStyle(_kbSort === key)}" ${action('setKbSort', key)}>${esc(label)}</button>`
  return (
    btn('updated_desc', 'Updated \u2193') +
    btn('updated_asc', 'Updated \u2191') +
    btn('created_desc', 'Created \u2193') +
    btn('created_asc', 'Created \u2191')
  )
}

function renderFilterBar(): string {
  const hasFilters = _kbTypeFilter !== null || _kbTagFilter.size > 0 || _kbSearch !== ''
  if (!hasFilters) {
    return ''
  }
  const tagChips = [..._kbTagFilter]
    .map(
      (tag) =>
        `<button aria-label="Remove ${esc(tag)} tag filter" style="display:inline-flex;align-items:center;gap:4px;font-family:var(--mono);font-size:9px;padding:2px 8px;border-radius:99px;border:none;cursor:pointer;background:var(--cyn-d);color:var(--cyn)" ${action('toggleKbTag', tag)}>${esc(tag)} <span aria-hidden="true" style="opacity:.6">\u00d7</span></button>`,
    )
    .join('')
  const typeChip = _kbTypeFilter
    ? `<span style="font-family:var(--mono);font-size:9px;padding:2px 8px;border-radius:99px;background:var(--pur-d);color:var(--pur)">${esc(_kbTypeFilter)}</span>`
    : ''
  const searchChip = _kbSearch
    ? `<span style="font-family:var(--mono);font-size:9px;padding:2px 8px;border-radius:99px;background:var(--s3);color:var(--t3)">\u{1F50D} ${esc(_kbSearch)}</span>`
    : ''
  return `<div role="status" aria-label="Active filters" style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:12px;padding:7px 10px;background:var(--s1);border:1px solid var(--bd);border-radius:6px"><span style="font-family:var(--mono);font-size:9px;color:var(--t4)" aria-hidden="true">Active:</span>${typeChip}${searchChip}${tagChips}<button style="font-family:var(--mono);font-size:9px;padding:2px 8px;border-radius:99px;border:none;cursor:pointer;background:var(--err-d);color:var(--err);margin-left:auto" aria-label="Clear all active filters" ${action('clearKbFilters')}>Clear all</button></div>`
}

function renderKBList(): string {
  const tabBar = renderTabBar(_view)
  if (_view === 'usage') return tabBar
  const filtered = getFilteredEntries()
  const subtitleCount =
    filtered.length < _kbEntries.length
      ? `${filtered.length} of ${_kbEntries.length} entries`
      : `${_kbEntries.length} entries`
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const page = Math.max(0, Math.min(_kbPage, totalPages - 1))
  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  const searchInput = `<input id="kb-search" type="search" placeholder="Search title or body\u2026" value="${esc(_kbSearch)}" aria-label="Search KB entries by title or body" style="font-family:var(--mono);font-size:11px;padding:4px 10px;border-radius:7px;border:1px solid var(--bd);background:var(--s2);color:var(--t1);outline:none;min-width:180px;max-width:260px">`
  return (
    tabBar +
    `<header class="page-header"><div><h1 class="page-title">Knowledge Base</h1><p class="page-subtitle">Decisions, patterns, and learnings \u00b7 ${subtitleCount}</p></div><button class="cmd-k" ${action('openCommandPalette')}>Search <kbd>\u2318K</kbd></button></header><div style="display:flex;gap:6px;margin-bottom:8px;flex-wrap:wrap;align-items:center"><div role="group" aria-label="Filter by type" style="display:flex;gap:3px;padding:3px;background:var(--s2);border:1px solid var(--bd);border-radius:7px;flex-wrap:wrap">${renderTypeButtons()}</div></div><div style="display:flex;gap:6px;margin-bottom:14px;flex-wrap:wrap;align-items:center"><div role="group" aria-label="Sort order" style="display:flex;gap:3px;padding:3px;background:var(--s2);border:1px solid var(--bd);border-radius:7px">${renderSortButtons()}</div><div style="flex:1"></div>${searchInput}</div>${renderTagCloud(_kbEntries, _kbTagFilter)}${renderFilterBar()}${renderKBRows(pageItems, filtered, _kbTagFilter, _kbReadCounts)}${renderPaginationBar(page, totalPages)}`
  )
}

// ─── Interaction handlers (window-exposed) ──────────────────────────────────

export function setKbTypeFilter(type: string | null): void {
  // action() data-arg values are always strings; 'null' sentinel means clear filter.
  const resolved = type === 'null' ? null : type
  if (_kbTypeFilter === resolved) {
    return
  }
  _kbTypeFilter = resolved
  _kbPage = 0
  void loadKBList()
}

export function toggleKbTag(tag: string): void {
  const next = new Set(_kbTagFilter)
  if (next.has(tag)) {
    next.delete(tag)
  } else {
    next.add(tag)
  }
  _kbTagFilter = next
  _kbPage = 0
  const content = document.getElementById('kb-content')
  if (content) {
    content.innerHTML = renderKBList()
    wireKbSearchInput()
  }
}

export function setKbSort(sort: string): void {
  const valid = ['updated_desc', 'updated_asc', 'created_desc', 'created_asc'] as const
  if (!valid.includes(sort as typeof _kbSort)) {
    return
  }
  _kbSort = sort as typeof _kbSort
  _kbPage = 0
  const content = document.getElementById('kb-content')
  if (content) {
    content.innerHTML = renderKBList()
    wireKbSearchInput()
  }
}

export function setKbSearch(value: string): void {
  _kbSearch = value
  _kbPage = 0
  if (_kbSearchDebounce) {
    clearTimeout(_kbSearchDebounce)
  }
  _kbSearchDebounce = setTimeout(() => {
    void loadKBList()
  }, 250)
}

export function setKbPage(page: number): void {
  _kbPage = page
  const content = document.getElementById('kb-content')
  if (content) {
    content.innerHTML = renderKBList()
    wireKbSearchInput()
  }
}

export function clearKbFilters(): void {
  _kbTypeFilter = null
  _kbTagFilter = new Set()
  _kbSearch = ''
  _kbPage = 0
  void loadKBList()
}

export function goToKbWithTag(tag: string): void {
  _pendingTagFilter = tag
  if (location.hash === '#/kb') {
    // Hash won't change — hashchange event won't fire — drive the load directly.
    void renderKBListView()
  } else {
    navigate('/kb')
  }
}
